import { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import {
  cartStateForScope,
  selectWriteCartScope,
} from '../../redux/features/draftCart/cartScope.routing';

/**
 * The cart item the omnibar is acting on.
 *
 * HITL-1619 — reads the SCOPED cart rather than `state.cart`. Both carts number
 * `cartItemId` from 1, so once the agent has a My Cart line open,
 * `state.cart.cartItems[selectedItemCartId]` resolves to a DIFFERENT item — the
 * AI's line of the same number — and every omnibar edit would be aimed at it.
 *
 * `selectWriteCartScope` is `main` with `hitl_shadow_mode` off and `main` until
 * the agent actually opens a draft line, so this is the same hook it was on
 * every existing path.
 */
const useSelectedCartItems = () => {
  const cartScope = useAppSelector(selectWriteCartScope);
  const cart = useAppSelector((state: RootState) =>
    cartStateForScope(state, cartScope)
  );
  const selectedItem =
    useAppSelector((state) => state.dialog.selectedItem) ||
    Object.values(cart.cartItems)[Object.values(cart.cartItems).length - 1];

  const selectedCartItem = useMemo(() => {
    if (!selectedItem) return undefined;

    return cart.cartItems?.[selectedItem?.cartItemId];
  }, [selectedItem, cart.cartItems]);

  const dialogSelectedItemCartId = useAppSelector(
    (state) => state.dialog.selectedItemCartId
  );

  const selectedItemCartId = (() => {
    if (
      typeof dialogSelectedItemCartId === 'number' &&
      dialogSelectedItemCartId
    ) {
      return dialogSelectedItemCartId;
    }

    if (selectedItem && typeof selectedItem.cartItemId === 'number') {
      return selectedItem.cartItemId;
    }

    // Fallback
    return 0;
  })();

  return {
    cart,
    cartScope,
    selectedItem,
    selectedCartItem,
    selectedItemCartId,
  };
};

export default useSelectedCartItems;
